import { Global } from '@emotion/react';
import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { viewHistory } from '@/modules/api';
import { useEvent, useTheme } from '@/modules/common';
import { ErrorType, MessageReasonType, MessageSenderType, MessageType, SendToModelStatusType } from '@/modules/models';
import { FontLoader } from '@/modules/saywhatt-chat/components';
import { Widget } from '@/modules/widget';
import { useIsWidgetDisabled, useSemiOpenChat } from '@/modules/widget/hooks';
import {
  attachmentsAtom,
  bridgeAtom,
  errorAtom,
  isInitSentAtom,
  isOpenChatAtom,
  messagesAtom,
  messageTextAtom,
  useAtom,
  useValue,
} from '@/store';
import { AppEvents, getProductIdType } from '@/utils';
import { globalStyles } from './SaywhattChat.styles';

const Content = lazy(() => import('./components/content').then((module) => ({ default: module.Content })));

export type SaywhattChatProps = {
  onSendMessage(message: MessageType): Promise<{ error: ErrorType } | void>;
};

export const SaywhattChat = (props: SaywhattChatProps) => {
  const { onSendMessage } = props;

  const theme = useTheme();

  const isOpenChat = useValue(isOpenChatAtom);
  const { pageType, productId, variantId, onInit, abTestConfig, isEmbedded } = useValue(bridgeAtom);

  const [messages, setMessages] = useAtom(messagesAtom);
  const [error, setError] = useAtom(errorAtom);

  const [messageText, setMessageText] = useAtom(messageTextAtom);
  const attachments = useValue(attachmentsAtom);

  const isInitSent = useValue(isInitSentAtom);
  const pendingMessageRef = useRef<{
    messageText: string;
    selectedOptions?: string[];
  } | null>(null);

  const isViewHistorySent = useRef(false);
  const { onChatWindowClick } = useSemiOpenChat();
  const isWidgetDisabled = useIsWidgetDisabled();

  const handleSendMessage = useCallback(
    async (msg: MessageType): Promise<void> => {
      const sendResult = await onSendMessage(msg);

      if (sendResult?.error) {
        const updatedMessage = { ...msg, status: SendToModelStatusType.FAILED };

        setMessages((prev) => {
          const filteredMessages = prev.filter((m) => m.clientCorrelationId !== msg.clientCorrelationId);
          return [...filteredMessages, updatedMessage];
        });
      }
    },
    [onSendMessage, setMessages]
  );

  const resendMessage = useCallback(
    (msg: MessageType): void => {
      const updatedMessage = { ...msg, status: SendToModelStatusType.PENDING };

      setMessages((prev) => {
        const filteredMessages = prev.filter((message) => message.clientCorrelationId !== msg.clientCorrelationId);
        return [...filteredMessages, updatedMessage];
      });

      handleSendMessage(updatedMessage);
    },
    [handleSendMessage, setMessages]
  );

  const removeMessage = useCallback(
    (msg: MessageType): void => {
      setMessages((prev) => prev.filter((message) => message.clientCorrelationId !== msg.clientCorrelationId));

      if ([ErrorType.MessageTooLong, ErrorType.MessageNotDelivered].includes(error!)) {
        setError(null);
      }
    },
    [error, setError, setMessages]
  );

  const sendNewMessage = useCallback(
    (val?: string, selectedOptions?: string[]): void => {
      if (!messageText?.length && !val && !attachments.length) {
        return;
      }

      const content = messageText || val || '';

      const { pageType: type, product_id: id } = getProductIdType({
        pageType,
        product_id: productId,
      });

      const newMessage = {
        content: content,
        clientCorrelationId: uuidv4(),
        role: MessageSenderType.USER,
        reason: selectedOptions ? MessageReasonType.SELECTION : MessageReasonType.USER_INPUT,
        status: SendToModelStatusType.PENDING,
        product_id: id,
        variant_id: variantId,
        page_type: type,
        options: selectedOptions,
        clientTimestamp: new Date().getTime(),
      } as MessageType;

      setMessages((prev) => [...prev, newMessage]);
      handleSendMessage(newMessage);
      setMessageText('');
    },
    [attachments, handleSendMessage, messageText, pageType, productId, setMessages, setMessageText, variantId]
  );

  const isHistoryNonEmpty = messages.length > 0;
  const onSendNewMessage = useCallback(
    (event: CustomEvent<{ messageText: string; selectedOptions?: string[] }>) => {
      const { messageText, selectedOptions } = event.detail;

      if (!isEmbedded && (!isInitSent || !isHistoryNonEmpty)) {
        // queue the message to be sent after the INITIALIZATION message is sent
        pendingMessageRef.current = { messageText, selectedOptions };
      } else {
        // if the INITIALIZATION message is already sent, send the message immediately
        sendNewMessage(messageText, selectedOptions);
      }
    },
    [isEmbedded, isInitSent, sendNewMessage, isHistoryNonEmpty]
  );

  // this effect resolves the pending message (if present)
  useEffect(() => {
    if (!isInitSent || !isHistoryNonEmpty || !pendingMessageRef.current) {
      return;
    }
    const { messageText, selectedOptions } = pendingMessageRef.current;
    pendingMessageRef.current = null;
    sendNewMessage(messageText, selectedOptions);
  }, [isInitSent, sendNewMessage, isHistoryNonEmpty]);

  useEvent({ name: AppEvents.SEND_NEW_MESSAGE, handler: onSendNewMessage });

  useEffect(() => {
    isViewHistorySent.current = false;
  }, [pageType]);

  useEffect(() => {
    onInit?.();
  }, [onInit]);

  useEffect(() => {
    if (!pageType || isViewHistorySent.current) {
      return;
    }

    void viewHistory({
      productId,
      pageType,
      abGroup: abTestConfig?.client?.group ?? null,
      abTestName: abTestConfig?.client?.uuid ?? null,
      mlAbGroup: abTestConfig?.ml?.group ?? null,
      mlAbTestName: abTestConfig?.ml?.uuid ?? null,
    });
    isViewHistorySent.current = true;
  }, [pageType, productId, abTestConfig, isViewHistorySent]);

  return (
    <>
      <FontLoader />
      <Global styles={globalStyles(theme)} />
      <Widget />

      {isOpenChat && (isEmbedded || !isWidgetDisabled) && (
        <Suspense fallback={null}>
          <Content
            sendMessage={sendNewMessage}
            resendMessage={resendMessage}
            removeMessage={removeMessage}
            onChatWindowClick={onChatWindowClick}
          />
        </Suspense>
      )}
    </>
  );
};
